import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  CHECKBOX_SHAPE,
  Checkbox,
  DSBottomSheet,
  DSButton,
  DSModal,
} from '@repo/design-system/index'
import { Dispatch, SetStateAction, useState } from 'react'
import { ISchedule } from '../../../../modules/apis/student/lesson/schedule/types'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_book_lessons, n_common } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { isWithin1Hour } from '../../../../modules/utils'
import { COLORS } from '../../../../modules/vars'

const TodayScheduleAlertModal = ({
  schedules,
  showTodayScheduleModal,
  setShowTodayScheduleModal,
  handleSubmit,
  showSidePopup,
}: {
  schedules: ISchedule[]
  showTodayScheduleModal: boolean
  setShowTodayScheduleModal: Dispatch<SetStateAction<boolean>>
  handleSubmit: (skip?: boolean) => void
  showSidePopup: () => void
}) => {
  const { t } = useTranslation()
  const [agreeList, setAgreeList] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { currentUser } = useCurrentUser()

  const isScheduleStartingWithin1Hour = (schedules: ISchedule[]): boolean => {
    if (schedules.length == 0) return false
    return schedules.some((schedule) => {
      return isWithin1Hour(
        schedule.selectedTime ? schedule.selectedTime.classtime : schedule.selectedTimeslot,
        currentUser.timezone
      )
    })
  }
  const hasWithInStartTimeWithIn15Minutes = isScheduleStartingWithin1Hour(schedules)

  const isMobile = useIsMobile()
  const allChecked = hasWithInStartTimeWithIn15Minutes ? agreeList.length == 2 : agreeList.length == 1

  const handleSubmitWithLoading = async (skip?: boolean) => {
    setIsSubmitting(true)
    try {
      setShowTodayScheduleModal(false)
      await handleSubmit(skip)
    } finally {
      setIsSubmitting(false)
    }
  }

  return isMobile ? (
    <DSBottomSheet
      id="showTodayScheduleModal"
      isOpen={showTodayScheduleModal}
      setIsOpen={setShowTodayScheduleModal}
      closeOnDim={true}
      closeButton={true}
      noDim={false}
    >
      <div>
        <div className="p-[20px]">
          <div className="Title2-18Bd">{t(n_book_lessons.modal_confirm.title)}</div>
        </div>
        <div className="mt-[8px] px-[20px]">
          <Checkbox
            shape={CHECKBOX_SHAPE.SQUARE}
            controlSelected={[agreeList, setAgreeList]}
            containerClassName={'flex flex-col items-start justify-start gap-y-[8px]'}
          >
            <Checkbox.Item value={1}>
              <span className="Body2-14Bd">{t(n_book_lessons.last_min_time)}</span>
              <span className="ml-[4px] text-gray-800">
                {t(n_book_lessons.modal_confirm.last_min_time_caution_no_restore)}
              </span>
            </Checkbox.Item>
            {hasWithInStartTimeWithIn15Minutes && (
              <Checkbox.Item value={2}>
                <span className="Body2-14Bd">{t(n_book_lessons.last_min_time)}</span>
                <span className="ml-[4px] text-gray-800">
                  {t(n_book_lessons.modal_confirm.last_min_time_caution_15_min)}
                </span>
              </Checkbox.Item>
            )}
          </Checkbox>
          <div className="mt-[32px]">
            <DSButton
              size={BUTTON_SIZE.LG}
              onClick={() => handleSubmitWithLoading(true)}
              disabled={!allChecked || isSubmitting}
              isLoading={isSubmitting}
            >
              <DSButton.Label>{t(n_book_lessons.booking_complete)}</DSButton.Label>
            </DSButton>
          </div>
        </div>
      </div>
    </DSBottomSheet>
  ) : (
    <DSModal
      id="showTodayScheduleModal"
      isOpen={showTodayScheduleModal}
      setIsOpen={setShowTodayScheduleModal}
      size="lg"
    >
      <DSModal.Title>{t(n_book_lessons.modal_confirm.title)}</DSModal.Title>
      <DSModal.Content>
        <Checkbox
          shape={CHECKBOX_SHAPE.SQUARE}
          controlSelected={[agreeList, setAgreeList]}
          containerClassName={'flex flex-col items-start justify-start gap-y-[8px]'}
        >
          <Checkbox.Item value={1}>
            <span className="Body2-14Bd">{t(n_book_lessons.last_min_time)}</span>
            <span className="ml-[4px] text-gray-800">
              {t(n_book_lessons.modal_confirm.last_min_time_caution_no_restore)}
            </span>
          </Checkbox.Item>
          {hasWithInStartTimeWithIn15Minutes && (
            <Checkbox.Item value={2}>
              <span className="Body2-14Bd">{t(n_book_lessons.last_min_time)}</span>
              <span className="ml-[4px] text-gray-800">
                {t(n_book_lessons.modal_confirm.last_min_time_caution_15_min)}
              </span>
            </Checkbox.Item>
          )}
        </Checkbox>
      </DSModal.Content>
      <DSModal.ButtonContainer direction="horizontal">
        <DSButton
          size={BUTTON_SIZE.MD}
          type={BUTTON_TYPE.OUTLINE}
          customBgColor={COLORS.WHITE}
          onClick={() => {
            showSidePopup()
            setShowTodayScheduleModal(false)
          }}
        >
          <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
        </DSButton>
        <DSButton
          size={BUTTON_SIZE.MD}
          onClick={() => handleSubmitWithLoading(true)}
          disabled={!allChecked || isSubmitting}
          isLoading={isSubmitting}
        >
          <DSButton.Label>{t(n_book_lessons.booking_complete)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

export default TodayScheduleAlertModal
